// ============================================================
// MongoDBDashboard.jsx
// ============================================================
// Junior-dev notes:
// - The 'mysqlTopics' were replaced with 'mongodbTopics'.
// - Theme colors changed from 'goldenrod' to 'MongoDB Green' (#00ED64).
// - Added NoSQL-specific concepts like Sharding and Aggregation.
// ============================================================

import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import StorageIcon from "@mui/icons-material/Storage";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import TerminalIcon from "@mui/icons-material/Terminal";

/* Custom Components (Assumed existence from your structure) */
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LectureTimer from "../../../components/LectureTimer";
import LanguageTabs from "../../../components/LanguageTabs";

/* Assets */
const bgImage = "https://images.unsplash.com/photo-1544383835-bda2bc66a55d";

/* ── Branded Styled Components ───────────────────────────── */
const MDB_GREEN = "#00ED64"; // Official MongoDB Green
const MDB_NAVY = "#001E2B";  // Official MongoDB Navy

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(0,30,43,0.9), rgba(0,30,43,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    color: "white",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
}));

const DashboardCard = styled(Card)(() => ({
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: "12px",
    overflow: "hidden",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(0, 237, 100, 0.1)",
        borderColor: MDB_GREEN,
    },
}));

const ViewButton = styled(Button)(() => ({
    backgroundColor: MDB_GREEN,
    color: MDB_NAVY,
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#00C351",
    },
}));

/* ── MONGODB MASTERY TOPICS ──────────────────────────────── */
const mongodbTopics = [
    { label: "Intro to NoSQL vs SQL Concepts", to: "/mongodb-intro" },
    { label: "The BSON Document Model & JSON", to: "/mongodb-bson-json" },
    { label: "Installing MongoDB & Compass GUI", to: "/mongodb-installation" },
    { label: "The Mongo Shell (mongosh) Basics", to: "/mongodb-shell" },
    { label: "Create & Insert Operations", to: "/mongodb-crud-create" },
    { label: "Querying Documents (Read Operations)", to: "/mongodb-crud-read" },
    { label: "Update Operators ($set, $inc, $push)", to: "/mongodb-crud-update" },
    { label: "Delete Operations & TTL Indexes", to: "/mongodb-crud-delete" },
    { label: "Schema Design: Embedding vs Referencing", to: "/mongodb-schema-design" },
    { label: "Aggregation Framework (Pipelines & Stages)", to: "/mongodb-aggregation" },
    { label: "Indexing for Performance (Single & Compound)", to: "/mongodb-indexing" },
    { label: "Text Search & Geospatial Queries", to: "/mongodb-advanced-queries" },
    { label: "Transactions & Atomicity in MongoDB", to: "/mongodb-transactions" },
    { label: "Replication (Replica Sets & High Availability)", to: "/mongodb-replication" },
    { label: "Sharding (Horizontal Scaling Concepts)", to: "/mongodb-sharding" },
    { label: "MongoDB Security & Role-Based Access", to: "/mongodb-security" },
    { label: "Backup, Export & Data Migration", to: "/mongodb-backup" },
    { label: "Integration with Node.js (Mongoose ODM)", to: "/mongodb-mongoose" },
    { label: "MongoDB Quiz & Mini Project", to: "/mongodb-project" },
    { label: "Certification Examination", to: "/mongodb-examination" },
];

/* ── MAIN COMPONENT ──────────────────────────────────────── */
export default function MongoDBDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    const [completedCount] = useState(0);

    const filteredTopics = useMemo(
        () =>
            mongodbTopics.filter(topic =>
                topic.label.toLowerCase().includes(searchQuery.toLowerCase())
            ),
        [searchQuery]
    );

    const progressPercentage = (completedCount / mongodbTopics.length) * 100;

    React.useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <PageWrapper>
            {/* Branded Banner */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 900,
                        color: MDB_GREEN,
                        px: 6,
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        letterSpacing: "2px"
                    }}
                >
                    MONGODB NOSQL
                </Typography>
            </Box>

            <LectureTimer />
            <LanguageTabs active="MongoDB" />

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* Video/Resource Panel */}
                <Grid item xs={12} md={5}>
                    <DashboardCard>
                        <CardMedia
                            component="iframe"
                            height={isMobile ? "200" : "280"}
                            src="https://www.youtube.com/embed/EE8ZTQ60yos" // MongoDB Crash Course
                            title="MongoDB Tutorial"
                            allowFullScreen
                        />
                        <CardContent sx={{ textAlign: "center", bgcolor: MDB_NAVY, color: "white" }}>
                            <Typography fontWeight={800} sx={{ color: MDB_GREEN }}>
                                Document-Oriented Mastery
                            </Typography>
                            <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 1, mb: 2 }}>
                                <StorageIcon fontSize="small" />
                                <Typography variant="body2">NoSQL Architecture</Typography>
                            </Stack>
                            <Link to="/mongodb-playground">
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={<TerminalIcon />}
                                    sx={{
                                        bgcolor: MDB_GREEN,
                                        color: MDB_NAVY,
                                        fontWeight: 800,
                                        "&:hover": { bgcolor: "white" }
                                    }}
                                >
                                    Open MongoDB Shell
                                </Button>
                            </Link>
                        </CardContent>
                    </DashboardCard>
                </Grid>

                {/* Curriculum Panel */}
                <Grid item xs={12} md={7}>
                    <Typography textAlign="center" fontWeight={700} sx={{ color: "white", mb: 2 }}>
                        MONGODB PROFESSIONAL CURRICULUM
                    </Typography>

                    <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{
                            my: 2,
                            height: 10,
                            borderRadius: 5,
                            bgcolor: "rgba(255,255,255,0.1)",
                            "& .MuiLinearProgress-bar": {
                                bgcolor: MDB_GREEN,
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search MongoDB module..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: MDB_GREEN }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 3,
                            "& .MuiInputBase-root": { color: "white", bgcolor: "rgba(255,255,255,0.05)" },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" }
                        }}
                    />

                    <Stack spacing={1.5} sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>
                        {filteredTopics.map(topic => (
                            <TopicBox key={topic.to}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <RadioOption />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {topic.label}
                                    </Typography>
                                </Stack>
                                <Link to={topic.to} style={{ textDecoration: "none" }}>
                                    <ViewButton size="small">View</ViewButton>
                                </Link>
                            </TopicBox>
                        ))}
                    </Stack>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{
                        position: "fixed",
                        bottom: 25,
                        right: 25,
                        bgcolor: MDB_GREEN,
                        color: MDB_NAVY,
                        "&:hover": { bgcolor: "white" }
                    }}
                >
                    <KeyboardArrowUpIcon />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
